import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Home, ShoppingCart, Store, User, Package, Users, CreditCard, BarChart3, Bell, Settings, Search, ChevronDown, Menu, LogOut, Megaphone, MapPin, Coins, KeyRound,
} from 'lucide-react';
import Logo, { TocoaScript } from '../components/Logo';
import { useApp } from '../context/AppContext';
import { IMAGES } from '../lib/constants';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const NAV = [
  { to: '/admin', label: 'Dashboard', icon: Home, end: true },
  { to: '/admin/pedidos', label: 'Pedidos', icon: ShoppingCart, badge: true },
  { to: '/admin/restaurantes', label: 'Restaurantes', icon: Store },
  { to: '/admin/conductores', label: 'Conductores', icon: User },
  { to: '/admin/estadisticas-conductores', label: 'Estadísticas de conductores', icon: BarChart3 },
  { to: '/admin/pago-y-asignacion', label: 'Pago y asignación', icon: Coins },
  { to: '/admin/avisos-conductores', label: 'Avisos a conductores', icon: Megaphone },
  { to: '/admin/promociones-clientes', label: 'Promociones a clientes', icon: Megaphone },
  { to: '/admin/mandaditos', label: 'Mandaditos 7', icon: Package },
  { to: '/admin/usuarios', label: 'Usuarios', icon: Users },
  { to: '/admin/pagos', label: 'Pagos y comprobantes', icon: CreditCard },
  { to: '/admin/reportes', label: 'Reportes', icon: BarChart3 },
  { to: '/admin/notificaciones', label: 'Notificaciones', icon: Bell },
  { to: '/admin/accesos', label: 'Accesos y contraseñas', icon: KeyRound },
  { to: '/admin/configuracion', label: 'Configuración', icon: Settings },
  { to: '/admin/zonas', label: 'Zonas y tarifas', icon: MapPin },
  { to: '/admin/tarifas-encomiendas', label: 'Tarifas de encomiendas', icon: Package },
];

const Sidebar = ({ onNavigate, pendingCount }) => (
  <aside className="w-[264px] shrink-0 bg-[#1E2130] text-white h-screen overflow-y-auto no-scrollbar flex flex-col">
    <div className="px-5 pt-5 pb-4"><Logo size="md" dark /></div>
    <nav className="px-3 flex flex-col gap-1 flex-1">
      {NAV.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={onNavigate}
          data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-xl text-[15px] font-medium transition-colors ${
              isActive ? 'bg-[#EE3B36] text-white shadow-md shadow-red-900/30' : 'text-gray-300 hover:bg-white/5 hover:text-white'
            }`
          }
        >
          <n.icon size={20} />
          <span className="flex-1">{n.label}</span>
          {n.badge && pendingCount > 0 && (
            <span className="bg-[#EE3B36] text-white text-xs font-bold px-2 py-0.5 rounded-md">{pendingCount}</span>
          )}
        </NavLink>
      ))}
    </nav>
    <div className="m-3 rounded-2xl overflow-hidden relative h-36 bg-[#2A2D3A]">
      <img src={IMAGES.rider2} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1E2130] via-[#1E2130]/40 to-transparent" />
      <div className="absolute bottom-3 left-4 right-4">
        <TocoaScript light className="text-3xl leading-none" />
        <p className="font-script text-lg text-white/90 leading-tight">Tu ciudad, en tus manos</p>
      </div>
    </div>
  </aside>
);

const AdminLayout = () => {
  const { admin, signOut, orders, notifications, markAllRead, connected } = useApp();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const pending = orders.filter((o) => ['pending', 'preparing', 'ready', 'on_way'].includes(o.status)).length;
  const unread = notifications.filter((n) => !n.read).length;

  React.useEffect(() => {
    if (!admin) navigate('/admin/login', { replace: true });
  }, [admin, navigate]);

  if (!admin) return null;

  const logout = () => { signOut(); navigate('/admin/login'); };

  return (
    <div className="min-h-screen flex bg-[#F4F5F8]">
      <div className="hidden lg:block sticky top-0 h-screen self-start"><Sidebar pendingCount={pending} /></div>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-[264px] border-0">
          <Sidebar onNavigate={() => setOpen(false)} pendingCount={pending} />
        </SheetContent>
      </Sheet>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-[#F4F5F8]/80 backdrop-blur sticky top-0 z-30 flex items-center gap-4 px-4 md:px-7">
          <button className="lg:hidden p-2 rounded-lg hover:bg-white" onClick={() => setOpen(true)} aria-label="Abrir menú" data-testid="open-sidebar"><Menu size={22} /></button>
          <div className="relative flex-1 max-w-[430px]">
            <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              data-testid="admin-search"
              placeholder="Buscar pedidos, restaurantes, conductores, usuarios..."
              className="w-full h-10 pl-10 pr-4 rounded-xl bg-white border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#EE3B36]/30 focus:border-[#EE3B36]"
            />
          </div>
          <div className="ml-auto flex items-center gap-4">
            <span className={`hidden md:flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${connected ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`} data-testid="live-indicator"><span className={`w-2 h-2 rounded-full ${connected ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`} />{connected ? 'En vivo' : 'Reconectando...'}</span>
            <Popover>
              <PopoverTrigger asChild>
                <button className="relative p-2 rounded-lg hover:bg-white transition-colors" aria-label="Notificaciones" data-testid="notif-bell">
                  <Bell size={22} className="text-[#1E2130]" />
                  {unread > 0 && <span className="absolute -top-0.5 -right-0.5 bg-[#EE3B36] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">{unread}</span>}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0">
                <div className="px-4 py-3 border-b flex items-center justify-between">
                  <p className="font-bold text-sm">Notificaciones</p>
                  <button className="text-xs text-[#EE3B36] font-semibold" onClick={markAllRead}>Marcar leídas</button>
                </div>
                <div className="max-h-80 overflow-auto">
                  {notifications.slice(0, 5).map((n) => (
                    <div key={n.id} className={`px-4 py-3 border-b last:border-0 ${n.read ? '' : 'bg-[#FFF7F7]'}`}>
                      <p className="text-sm font-semibold">{n.title}</p>
                      <p className="text-xs text-gray-500 [overflow-wrap:anywhere]">{n.desc}</p>
                      <p className="text-[11px] text-gray-400 mt-1">{n.time}</p>
                    </div>
                  ))}
                </div>
                <button className="w-full py-2.5 text-sm font-semibold text-[#EE3B36] hover:bg-gray-50" onClick={() => navigate('/admin/notificaciones')}>Ver todas</button>
              </PopoverContent>
            </Popover>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2.5 hover:bg-white rounded-xl px-2 py-1.5 transition-colors" data-testid="admin-user-menu">
                  <div className="w-9 h-9 rounded-full bg-[#E9E4FF] text-[#6B5BD2] font-bold text-sm flex items-center justify-center">AR</div>
                  <span className="hidden sm:block font-semibold text-[#1E2130] text-sm">{admin.name || 'Administrador'}</span>
                  <ChevronDown size={16} className="text-gray-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/admin/configuracion')}><Settings size={16} className="mr-2" /> Configuración</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="admin-logout"><LogOut size={16} className="mr-2" /> Cerrar sesión</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main key={location.pathname} className="px-4 md:px-7 pb-8 fade-up">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
